
import React, { useState, useEffect, useMemo } from 'react';
import { ServiceItem, ReportMetadata, UserProfile, ServiceAssignment, ServiceTemplate } from '../types';
import { Button } from './Button';
import { X, Save, Plus, Trash2, FilePlus, Loader2, Lock, Unlock, Search, CheckSquare, Square, Database, Calculator, LayoutTemplate, AlertTriangle, ListChecks } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { loadProjectById } from '../services/cloudService';
import { calculateProjectedEndDate } from '../services/dateService';
import { applyMinHoursInput, isHourlyUnit } from '../services/projectLv';
import { bestandteileGeaendert, diffSupplementServices, NachtragKonflikt, paketLoeschSperre, prepareSupplementServices, rebaseSupplementServices, rebaseUsedDeltas, supplementLeadingService, supplementNewCounter, supplementProofCount, supplementUsedInspections, vermuteteEintragsKeys } from './projectSupplementLogic';
import { BestandteileEditor } from './bestandteile/BestandteileEditor';
import { StundenKontingentFeld } from './bestandteile/StundenKontingentFeld';
import { bestandteilePatch, definitionsFehlerImLv, positionsSperreFuerBestandteile } from './bestandteile/bestandteileEditorLogic';
import { hatBestandteile, kopiereVorlage, normalisiereBestandteile } from '../services/bestandteile/bestandteilLogic';
import { kontingentFuerKopie } from '../services/bestandteile/kontingentKopie';
import { isAdminRole } from '../services/projectLockLogic';
import { createServiceItemFromSource, initialQuantityForSource } from '../services/serviceItemDraft';
import {
    FlatInclusionDecision,
    applyFlatInclusionDecisions,
    pendingFlatInclusionDecisions,
} from '../services/flatRateLogic';
import { getServiceLog, JournalMirrorRequest, mirrorEntriesToJournal } from '../services/serviceJournal';
import type { ListProject } from '../services/listProject';

interface Props {
    /**
     * Ein Eintrag der Projektliste (AdminProjectList, AdminView) — ein Auszug.
     * Gespeichert wird deshalb nie aus ihm, sondern aus dem beim Speichern
     * frisch geladenen Stand (loadProjectById in handleSave).
     */
    project: ListProject;
    catalog: ServiceItem[]; 
    currentUser: UserProfile | null;
    onSave: (updatedProject: any, changeLog: string, syncCounters?: { current: number, exported: number }) => Promise<void>;
    onClose: () => void;
}

/**
 * Die Zweitschrift der Korrekturbewegungen ins Buchungsjournal (S4b) — aber
 * erst nach dem NACHWEIS, dass das Speichern wirklich gelungen ist.
 *
 * Der Nachweis ist hier noetig und nicht ueberfluessig: beide Einhaengepunkte
 * des Dialogs (AdminProjectList, AdminView) fangen den Fehler von `onSave`
 * SELBST ab und zeigen ihn dem Anwender. Die Zusage kommt deshalb auch nach
 * einem gescheiterten Nachtrag erfuellt zurueck, und ein blindes Anhaengen
 * schriebe eine Journalzeile, die das JSON nie bekommen hat. Die
 * Reparaturrichtung der Uebergangsphase ist ausdruecklich nur JSON -> Journal;
 * das Journal kennt weder Update noch Delete.
 *
 * Ein zweiter Lesevorgang auf einem selten benutzten Verwaltungsweg ist dafuer
 * der guenstigste Preis. Bleibt der Nachweis aus, wird NICHT angehaengt — die
 * sichere Richtung.
 */
const spiegleNachBestaetigung = async (req: JournalMirrorRequest): Promise<void> => {
    try {
        const gespeichert = await loadProjectById(req.projectId);
        const log: any[] = getServiceLog(gespeichert?.data);
        const vorhanden = new Set(log.map(e => e?.id));
        if (!req.entries.every(e => vorhanden.has(e.id))) {
            console.warn('[serviceJournal] Nachtrag nicht bestaetigt — keine Zweitschrift geschrieben.');
            return;
        }
    } catch (e) {
        console.warn('[serviceJournal] Nachtrag nicht bestaetigt — keine Zweitschrift geschrieben.', e);
        return;
    }
    await mirrorEntriesToJournal(req);
};

const generateValidUUID = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const ProjectSupplementModal: React.FC<Props> = ({ project, catalog, currentUser, onSave, onClose }) => {
    const [services, setServices] = useState<ServiceItem[]>([]);
    const [initialServices, setInitialServices] = useState<ServiceItem[]>([]);
    const [unlockedUsedRows, setUnlockedUsedRows] = useState<Set<string>>(new Set());
    // Rohtext der Mindeststunden-Felder je Position. Getrennt vom Modellwert:
    // aus ihm zurueckgespeist verschwaende die Zwischenstufe "0" beim Tippen
    // von "0.25" sofort, und die naechsten Ziffern ergaeben 25.
    const [minHoursText, setMinHoursText] = useState<Record<string, string>>({});
    
    const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
    const [catalogSearch, setCatalogSearch] = useState('');
    const [localCatalog, setLocalCatalog] = useState<ServiceItem[]>([]);
    // Benannte LV-Vorlagen (service_templates). Bisher konnte nur der
    // Anlage-Wizard sie lesen; ein BESTEHENDES Projekt kam ueber diesen Dialog
    // ausschliesslich an den Gesamt-Katalog. Leere Liste = kein Umschalter,
    // der Dialog verhaelt sich dann exakt wie bisher.
    const [templates, setTemplates] = useState<ServiceTemplate[]>([]);
    // '' bedeutet Gesamt-Katalog — die Vorbelegung und damit das Altverhalten.
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [selectionBuffer, setSelectionBuffer] = useState<Map<string, number>>(new Map());
    // MONATSPAUSCHALE — die ausdrueckliche Entscheidung je NEUER Position:
    // "in der Monatspauschale enthalten: ja / nein". BEWUSST ohne Vorbelegung
    // (Betreiber-Entscheidung 3): eine fehlende Entscheidung sperrt das
    // Speichern, sie ist kein Wert. Regel und Umschreibung der Deckung liegen
    // als pure Funktionen in flatRateLogic (pendingFlatInclusionDecisions /
    // applyFlatInclusionDecisions) — der Dialog haelt nur den Zustand.
    const [flatDecisions, setFlatDecisions] = useState<Record<string, FlatInclusionDecision>>({});
    // Eigene Berichte, die beim Oeffnen als Nachweiszeile auf der Pauschale
    // standen (Pauschalprojekt). Sie gehoeren zum Zaehler, aber nicht auf die
    // Begehungsposition — siehe projectSupplementLogic.supplementProofCount.
    const [proofCount, setProofCount] = useState(0);
    // Berichtszaehler beim Oeffnen und ob ein Nachtrag ihn an die Positionen
    // angleicht (projectSupplementLogic.prepareSupplementServices). Weicht er
    // ab, zeigt der Dialog nur einen Hinweis und laesst ihn stehen.
    const [zaehlerStand, setZaehlerStand] = useState({ live: 0, positionen: 0, folgt: true });
    // PAKETPOSITION: Zeilen, deren Bestandteile aufgeklappt sind. Festlegen
    // darf nur ein Admin (E6); alle anderen sehen die Liste nur lesend.
    const [offeneBestandteile, setOffeneBestandteile] = useState<Set<string>>(new Set());
    // Zeile, an der zuletzt ein gesperrtes Loeschen versucht wurde — dort
    // steht der Grund (paketLoeschSperre) als Hinweis unter der Zeile.
    const [loeschHinweisId, setLoeschHinweisId] = useState<string | null>(null);
    const isAdmin = isAdminRole(currentUser?.role);

    useState<{
        lvPosition: string;
        title: string;
        unit: string;
        unitPrice: number;
        category: string;
        isDefault: boolean;
        assignment: ServiceAssignment;
    }>({
        lvPosition: '', title: '', unit: 'Stück', unitPrice: 0, category: 'Ausführung', isDefault: false, assignment: 'none'
    });
    
    const [loading, setLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    // Leistungsdatum einer IST-Stand-Korrektur. Ohne Datum kennt das
    // Leistungsverzeichnis nur eine Gesamtsumme, und die Menge taucht in keiner
    // Zeitraumabrechnung auf — sie ist offen, aber keinem Monat zuzuordnen.
    // Vorbelegt mit heute; korrigiert man einen laenger zurueckliegenden Stand,
    // gehoert das tatsaechliche Leistungsdatum hierher, sonst landet die Menge
    // im falschen Abrechnungsmonat.
    const [correctionDate, setCorrectionDate] = useState(() =>
        new Date().toISOString().slice(0, 10)
    );

    useEffect(() => {
        const initData = async () => {
            if (project) {
                const fullProject = await loadProjectById(project.id);
                const dataToUse = fullProject ? fullProject.data : project.data;
                const meta = dataToUse.metadata as ReportMetadata;
                const rawServices = meta.globalData?.services || [];
                
                const liveCount = fullProject?.current_inspection_number || project.current_inspection_number || meta.inspectionNumber || 0;

                const servicesWithIds = rawServices.map((s: any) => ({
                    ...s,
                    id: s.id ? String(s.id) : generateValidUUID(),
                    quantityUsed: s.quantityUsed || 0,
                }));
                // Kein Abgleich mit dem Zaehler mehr (18.10.2026): der Dialog
                // zeigt den IST-Stand, wie er ist. Die fruehere Uebernahme des
                // Zaehlers auf die Begehungsposition schrieb bei jedem
                // Speichern die Differenz als Korrektur ins Protokoll —
                // Begruendung an prepareSupplementServices.
                const { services: rohStand, proofCount: proofs, usedInspections: positionen, counterFollows } = prepareSupplementServices(
                    servicesWithIds,
                    liveCount,
                    getServiceLog(dataToUse),
                );

                setProofCount(proofs);
                setZaehlerStand({ live: liveCount, positionen: positionen + proofs, folgt: counterFollows });
                setServices(JSON.parse(JSON.stringify(rohStand)));
                // Abgleichsgrundlage ist derselbe rohe Stand: eine Korrektur
                // entsteht nur aus einer Aenderung des Bedieners (IST-Feld
                // entsperrt, neue Position mit IST-Stand).
                setInitialServices(JSON.parse(JSON.stringify(servicesWithIds)));
            }
        };
        initData();
    }, [project?.id]); 

    useEffect(() => {
        const loadCatalog = async () => {
            if (catalog && catalog.length > 0) {
                setLocalCatalog(catalog);
                return;
            }
            try {
                const { data } = await supabase.from('service_catalog').select('*').order('lv_position', { ascending: true });
                if (data) {
                    const mappedItems: ServiceItem[] = data.map((item: any) => {
                        // Bestandteil-Vorlage der Katalogposition (Nr. 165).
                        // Fehlt die Spalte, ist das Feld undefined — der
                        // Dialog arbeitet dann wie bisher.
                        const vorlage = kopiereVorlage(normalisiereBestandteile(item.bestandteile));
                        return {
                            id: item.id, lvPosition: item.lv_position, title: item.title, unit: item.unit, unitPrice: item.unit_price, category: item.category, isDefault: item.is_default, quantityContracted: 0, quantityUsed: 0, status: 'open', assignment: item.assignment || 'none',
                            ...(vorlage ? { bestandteile: vorlage } : {}),
                            // Stundenkontingent (service_catalog.stunden_kontingent,
                            // Nr. 171) — NUR mit der Vorlage (S1); fehlt die
                            // Spalte, bleibt das Feld weg.
                            ...kontingentFuerKopie({ bestandteile: vorlage }, item.stunden_kontingent),
                        };
                    });
                    setLocalCatalog(mappedItems);
                }
            } catch (e) { console.error(e); }
        };
        loadCatalog();
    }, [catalog]);

    useEffect(() => {
        // Vorlagen sind eine Zugabe, keine Voraussetzung: schlaegt die Abfrage
        // fehl (fehlende Tabelle, Netz, Rechte), bleibt die Liste leer, der
        // Umschalter erscheint gar nicht erst und der Dialog arbeitet mit dem
        // Gesamt-Katalog weiter. Ein Abbruch hier wuerde den Nachtrag als
        // Ganzes unbrauchbar machen.
        const loadTemplates = async () => {
            try {
                const { data, error } = await supabase.from('service_templates').select('*');
                if (error) throw error;
                if (data) {
                    setTemplates(data.map((t: any) => ({
                        id: String(t.id),
                        name: t.name || 'Ohne Namen',
                        description: t.description || undefined,
                        // items ist jsonb und kann null oder Unsinn sein.
                        items: Array.isArray(t.items) ? (t.items as ServiceItem[]) : [],
                    })));
                }
            } catch (e) { console.error(e); }
        };
        loadTemplates();
    }, []);

    const toggleUsedLock = (id: string) => {
        const newSet = new Set(unlockedUsedRows);
        if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
        setUnlockedUsedRows(newSet);
    };

    const handleQuantityChange = (index: number, newQtyStr: string) => {
        const newQty = parseFloat(newQtyStr);
        if (isNaN(newQty) || newQty < 0) return;
        setServices(prev => prev.map((s, i) => i === index ? { ...s, quantityContracted: newQty } : s));
    };

    const handleUsedQuantityChange = (index: number, newUsedStr: string) => {
        const newUsed = parseFloat(newUsedStr);
        if (isNaN(newUsed) || newUsed < 0) return;
        setServices(prev => prev.map((s, i) => i === index ? { ...s, quantityUsed: newUsed } : s));
    };

    const handlePriceChange = (index: number, newPriceStr: string) => {
        const newPrice = parseFloat(newPriceStr);
        if (isNaN(newPrice) || newPrice < 0) return;
        setServices(prev => prev.map((s, i) => i === index ? { ...s, unitPrice: newPrice } : s));
    };
    
    const updateService = (index: number, field: keyof ServiceItem, value: any) => {
        setServices(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
    };

    // Mehrere Felder auf einmal — die Bestandteile und das Kontingent, das
    // mit ihnen faellt (S1, bestandteilePatch).
    const patchService = (index: number, patch: Partial<ServiceItem>) => {
        setServices(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s));
    };

    const handleImmediateDelete = async (serviceId: string, index: number) => {
        if (!serviceId) {
            setServices(prev => prev.filter((_, i) => i !== index));
            return;
        }

        // Paketposition mit Buchung oder erledigten Bestandteilen: nicht hier
        // loeschen — die Kaskade naehme den Nachweis mit (paketLoeschSperre).
        const sperre = paketLoeschSperre(initialServices.find(s => String(s.id) === String(serviceId)));
        if (sperre) {
            setLoeschHinweisId(String(serviceId));
            return;
        }

        if (!window.confirm("Soll diese Position sofort und unwiderruflich gelöscht werden?")) return;

        setLoading(true);
        try {
            const { error: sqlError } = await supabase
                .from('project_services')
                .delete()
                .eq('id', serviceId);

            if (sqlError) throw sqlError;

            const { data: currentProject } = await supabase
                .from('projects')
                .select('data')
                .eq('id', project.id)
                .single();

            if (currentProject && currentProject.data?.metadata?.globalData?.services) {
                const meta = currentProject.data.metadata;
                const currentJsonServices = meta.globalData.services;
                const cleanJsonServices = currentJsonServices.filter((s: any) => s.id !== serviceId);
                const newData = {
                    ...currentProject.data,
                    metadata: {
                        ...meta,
                        globalData: {
                            ...meta.globalData,
                            services: cleanJsonServices
                        }
                    }
                };
                await supabase.from('projects').update({ data: newData }).eq('id', project.id);
            }

            setServices(prev => prev.filter((_, i) => i !== index));
            setInitialServices(prev => prev.filter(s => s.id !== serviceId));
        } catch (e: any) {
            console.error(e);
            alert("Fehler beim Löschen: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    // Rechnung in projectSupplementLogic (rein, getestet). Der neue Zaehler
    // entsteht erst beim Speichern: er braucht auch die Nachweise des
    // frischen Protokolls (supplementInspectionNr).
    const { changes, needsCounterSync, usedDeltas } = useMemo(
        () => diffSupplementServices(services, initialServices, flatDecisions),
        [services, initialServices, flatDecisions],
    );

    /** Ids der in DIESEM Dialog neu aufgenommenen Positionen. */
    const newServiceIds = useMemo(() => {
        const known = new Set(initialServices.map(s => String(s.id)));
        return services.filter(s => !known.has(String(s.id))).map(s => s.id);
    }, [services, initialServices]);

    // Welche neuen Positionen brauchen die Pauschal-Entscheidung (mit leeren
    // Entscheidungen gefragt: ALLE entscheidungspflichtigen, fuer die Anzeige)
    // und welche davon sind noch offen (sperrt das Speichern). Die Regel
    // selbst liegt in flatRateLogic.
    const flatDecisionIds = useMemo(
        () => new Set(pendingFlatInclusionDecisions(services, newServiceIds, {})),
        [services, newServiceIds],
    );
    const pendingFlatIds = useMemo(
        () => new Set(pendingFlatInclusionDecisions(services, newServiceIds, flatDecisions)),
        [services, newServiceIds, flatDecisions],
    );

    const initialById = useMemo(() => new Map(initialServices.map(s => [String(s.id), s])), [initialServices]);

    // PAKETPOSITION: Bestandteile, die nicht zur Position passen (E5: nur
    // Menge 1, nicht auf einer Pauschale), sperren das Speichern. Geprueft
    // wird nur, was in DIESEM Dialog neu ist oder an Menge bzw. Bestandteilen
    // geaendert wurde — ein Altfall im LV sperrte sonst jeden Nachtrag.
    const bestandteilFehler = useMemo(() => {
        const zuPruefen = new Set(services
            .filter(s => {
                const anfang = initialById.get(String(s.id));
                return !anfang || anfang.quantityContracted !== s.quantityContracted || bestandteileGeaendert(anfang, s);
            })
            .map(s => String(s.id)));
        return definitionsFehlerImLv(services, zuPruefen);
    }, [services, initialById]);

    const toggleBestandteile = (id: string) => {
        setOffeneBestandteile(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleSave = async () => {
        // Ohne Entscheidung kein Anlegen (Betreiber-Entscheidung 3). Der Knopf
        // ist zwar gesperrt, aber der Riegel gehoert AUCH hierher: der Zustand
        // kann sich zwischen Render und Klick aendern, und ein zweiter
        // Aufrufer des Handlers kennt den Knopf nicht.
        if (pendingFlatIds.size > 0) {
            alert('Für jede neue Position muss entschieden werden: in der Monatspauschale enthalten — ja oder nein.');
            return;
        }
        if (bestandteilFehler.length > 0) {
            // Die Meldung steht rot neben dem (gesperrten) Speichern-Knopf und
            // an der Zeile selbst — hier nur der Riegel.
            return;
        }
        setLoading(true);
        setIsSyncing(true);
        // Hinter dem try, dessen catch dem Anwender einen Fehler zeigt: das
        // Anhaengen ans Journal darf keine Meldung zu einem Nachtrag ausloesen,
        // der laengst geschrieben ist.
        let spiegelung: JournalMirrorRequest | null = null;
        try {
            if (changes.length === 0) { onClose(); return; }
            const freshProject = await loadProjectById(project.id);
            if (!freshProject) throw new Error("Projekt nicht mehr verfügbar.");
            const meta = freshProject.data.metadata as ReportMetadata;
            const updatedMeta = { ...meta };
            if (!updatedMeta.globalData) updatedMeta.globalData = { status: 'active', services: [], projectId: project.id };

            // Die Aenderungen des Bedieners auf den FRISCHEN Stand legen, nicht
            // den Stand vom Oeffnen zurueckschreiben — sonst verschwaende ein
            // waehrend des offenen Dialogs versendeter Bericht oder
            // ausgestellter Beleg (rebaseSupplementServices).
            const freshServices = ((meta.globalData?.services || []) as ServiceItem[])
                .map(s => ({ ...s, id: s.id ? String(s.id) : s.id, quantityUsed: s.quantityUsed || 0 }));
            const rebased = rebaseSupplementServices(services, initialServices, freshServices);

            let syncCounters = undefined;
            // Positionen PLUS Nachweise eigener Berichte auf der Pauschale —
            // nur die Positionen setzten den Zaehler im Pauschalprojekt
            // zurueck, und Berichtsnummern gingen doppelt raus. Derselbe Anteil
            // wie beim Oeffnen (supplementProofCount). Passte der Zaehler beim
            // Oeffnen nicht zu den Positionen, bleibt er stehen (null); unter
            // den frischen Zaehler geht er nie.
            const newInspectionNr = supplementNewCounter(
                { usedInspections: supplementUsedInspections(rebased), needsCounterSync },
                {
                    counterFollows: zaehlerStand.folgt,
                    currentCounter: freshProject.current_inspection_number || meta.inspectionNumber || 0,
                    proofCounts: [proofCount, supplementProofCount(rebased, getServiceLog(freshProject.data))],
                },
            );
            if (newInspectionNr !== null) {
                updatedMeta.inspectionNumber = newInspectionNr;
                updatedMeta.isExported = true; 
                syncCounters = { current: newInspectionNr, exported: freshProject.last_exported_inspection_number || (newInspectionNr - 1) };
            }

            // Ohne die Monatspauschale — ihre Vertragsmenge sind Monate.
            const leadingService = supplementLeadingService(rebased);
            if (leadingService) {
                if (leadingService.quantityContracted > 0) {
                    const startDate = updatedMeta.globalData.startDate || new Date().toISOString().split('T')[0];
                    const interval = meta.intervalConfig || { type: 'weekly' };
                    const newEndDate = calculateProjectedEndDate(startDate, leadingService.quantityContracted, interval);
                    if (newEndDate && newEndDate !== updatedMeta.globalData.endDate) updatedMeta.globalData.endDate = newEndDate;
                }
            }

            const newNote = { id: crypto.randomUUID(), text: changes.join(', '), date: new Date().toISOString(), author: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin' };

            // Datierte Bewegung je geaendertem IST-Stand. Ohne sie kennt nur das
            // Leistungsverzeichnis die Menge — als Gesamtsumme ohne Datum. Eine
            // Zeitraumabrechnung findet sie dann nicht, die Position fehlt auf
            // der Monatsrechnung, und niemand bemerkt es: eine fehlende Zeile
            // sieht aus wie eine, die es nicht gibt.
            const performerName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin';
            const correctionLog = rebaseUsedDeltas(usedDeltas, freshServices).map((d) => ({
                id: crypto.randomUUID(),
                date: correctionDate,
                serviceId: d.serviceId,
                quantity: d.delta,
                performerId: currentUser?.id || '',
                performerName,
                isSubstitution: false,
                description: `Korrektur des Leistungsstands (${d.from} → ${d.to})`,
            }));

            // Die getroffenen ja/nein-Entscheidungen in die Deckung der
            // Pauschalposition schreiben — erst hier, damit der Dialogzustand
            // bis zum Speichern unveraendert bleibt und ein Abbruch nichts
            // hinterlaesst. Ohne Pauschale und ohne Entscheidungen gibt die
            // Funktion dieselbe Liste zurueck.
            const servicesToSave = applyFlatInclusionDecisions(rebased, flatDecisions);

            const existingLog = freshProject.data.metadata.globalData?.serviceLog || [];
            const finalProjectData: any = { ...freshProject.data, metadata: { ...updatedMeta, globalData: { ...updatedMeta.globalData, services: servicesToSave, serviceLog: [...correctionLog, ...existingLog], notes: [newNote, ...(freshProject.data.metadata.globalData?.notes || [])] } } };
            await onSave(finalProjectData, changes.join(', '), syncCounters);
            // Quelle 'admin_accounting': das ist die Korrektur eines
            // Leistungsstands aus der Verwaltung, keine Verbuchung erbrachter
            // Arbeit ('manual_billing' gehoert dem Buchungsdialog) und keine
            // Rueckschreibung aus einem Beleg.
            //
            // Keine `statements`: eine Korrektur kann sehr wohl eine fehlende
            // Begehung nachtragen, aber der Dialog weiss nur die Differenz, nie
            // wofuer sie steht. Erschliessen darf es die Abbildung.
            if (correctionLog.length > 0) {
                spiegelung = {
                    projectId: project.id,
                    entries: correctionLog,
                    previousLog: existingLog,
                    source: 'admin_accounting',
                    linkedProjectConfig: updatedMeta.globalData?.linkedProjectConfig ?? null,
                };
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(e instanceof NachtragKonflikt ? e.message : "Fehler beim Speichern des Nachtrags");
        } finally {
            setLoading(false);
            setIsSyncing(false);
        }

        if (spiegelung) await spiegleNachBestaetigung(spiegelung);
    };

    const totalContractValue = services.reduce((acc, s) => acc + (s.quantityContracted * s.unitPrice), 0);
    
    // Quelle der Auswahlliste: Gesamt-Katalog (Vorbelegung) oder die Positionen
    // EINER benannten Vorlage. Gleiche Aufloesung wie im Anlage-Wizard.
    const activeSourceItems = useMemo<ServiceItem[]>(() => {
        if (!selectedTemplateId) return localCatalog;
        return templates.find(t => t.id === selectedTemplateId)?.items || [];
    }, [selectedTemplateId, templates, localCatalog]);

    // Der Puffer ist nach LV-Nummer geschluesselt und traegt die Menge der
    // Quelle, aus der er befuellt wurde. Beim Wechsel muss er deshalb fallen —
    // sonst uebernaehme man eine Katalogzeile mit Vorlagenmenge oder umgekehrt.
    const handleSourceChange = (templateId: string) => {
        setSelectedTemplateId(templateId);
        setSelectionBuffer(new Map());
    };

    // MODIFIED availableCatalog: No filtering of existing positions, but adding a flag
    const filteredCatalog = useMemo(() => {
        const existingPositions = new Set(services.map(s => s.lvPosition));
        return activeSourceItems
            .map(c => ({
                ...c,
                isExisting: existingPositions.has(c.lvPosition)
            }))
            .filter(c => (c.title || '').toLowerCase().includes(catalogSearch.toLowerCase()) || (c.lvPosition || '').toLowerCase().includes(catalogSearch.toLowerCase()));
    }, [activeSourceItems, services, catalogSearch]);

    return (
        <div data-glass-view="projekt-dialog" className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col max-h-[90vh] overflow-hidden animate-scale-in relative">
                
                <div data-tutorial="leistung.nachtrag" className="bg-indigo-600 text-white p-6 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="font-bold text-xl flex items-center gap-2"><FilePlus className="w-6 h-6 text-indigo-200" /> Nachtrag & Daten-Korrektur</h3>
                        <p className="text-indigo-100 text-sm mt-1">{project?.name} ({project?.data.metadata.projectNumber})</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right hidden md:block">
                            <span className="block text-[11px] uppercase font-bold text-indigo-300">Neues Gesamtvolumen</span>
                            <span className="font-mono font-bold text-lg">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(totalContractValue)}</span>
                        </div>
                        <button onClick={onClose} className="hover:bg-white/10 p-1 rounded-full"><X className="w-6 h-6"/></button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                    {/* Nur ein Hinweis, bewusst ohne „angleichen": weicht der
                        Zaehler ab, ist er meist NICHT die Wahrheit (Altbestand,
                        gewollte Korrektur) — siehe prepareSupplementServices. */}
                    {!zaehlerStand.folgt && (
                        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>
                                Berichtszähler <b>{zaehlerStand.live}</b> weicht vom Stand der Begehungspositionen ab (<b>{zaehlerStand.positionen}</b>).
                                Der Dialog gleicht das nicht an: Speichern ändert weder den Zähler noch den IST-Stand.
                                Den IST-Stand nur gezielt über das Schloss der Zeile korrigieren.
                            </span>
                        </div>
                    )}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-100 text-slate-500 font-bold uppercase text-xs border-b border-slate-200">
                                <tr>
                                    <th className="p-4 w-16">Pos.</th>
                                    <th className="p-4">Leistung</th>
                                    <th className="p-4 w-28 text-right bg-indigo-50/30 border-l border-indigo-50">Preis (€)</th>
                                    <th className="p-4 w-24 text-center bg-indigo-50/50 border-r border-indigo-100">Vertrag</th>
                                    <th className="p-4 w-32 text-center bg-orange-50/50">IST (Cloud)</th>
                                    <th className="p-4 w-16">Einh.</th>
                                    <th className="p-4 w-24 text-center" title="Mindeststunden je Buchung (nur Stundenpositionen)">Mind. Std.</th>
                                    <th className="p-4 w-28 text-right">Gesamt</th>
                                    <th className="p-4 w-12"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {services.map((s, idx) => {
                                    const isUnlocked = unlockedUsedRows.has(s.id);
                                    const isOverrun = s.quantityUsed > s.quantityContracted;
                                    const rowTotal = s.quantityContracted * s.unitPrice;
                                    // PAKETPOSITION: Stand beim Oeffnen (dagegen rechnet die DB),
                                    // Loeschsperre und Schalter fuer den Bestandteil-Editor.
                                    const anfang = initialById.get(String(s.id));
                                    const loeschSperre = paketLoeschSperre(anfang);
                                    const mitBestandteilen = hatBestandteile(s);
                                    const zeigeBestandteile = mitBestandteilen || (isAdmin && s.assignment !== 'flatrate' && !s.flatRate);
                                    const bestandteileAuf = offeneBestandteile.has(String(s.id));
                                    const bestandteilFehlerZeile = bestandteilFehler.find(f => f.serviceId === String(s.id));
                                    return (
                                        <React.Fragment key={s.id}>
                                        <tr className={`hover:bg-slate-50 group ${isOverrun ? 'bg-red-50/30' : ''}`}>
                                            <td className="p-4 font-mono text-xs text-slate-400">
                                                <div>{s.lvPosition}</div>
                                                <input type="text" placeholder="Kunde" value={s.clientLvPosition || ''} onChange={(e) => updateService(idx, 'clientLvPosition', e.target.value)} className="w-full mt-1 text-[11px] border border-slate-200 rounded px-1 py-0.5 outline-none focus:border-indigo-300"/>
                                            </td>
                                            <td className="p-4 font-medium text-slate-700">
                                                {s.title}
                                                {s.assignment && s.assignment !== 'none' && (
                                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-bold uppercase border border-indigo-100">{s.assignment}</span>
                                                )}
                                                {zeigeBestandteile && (
                                                    <div className="mt-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleBestandteile(String(s.id))}
                                                            aria-expanded={bestandteileAuf}
                                                            className={`inline-flex items-center gap-1 text-[11px] font-semibold py-0.5 ${bestandteilFehlerZeile ? 'text-red-700' : mitBestandteilen ? 'text-indigo-700 hover:text-indigo-900' : 'text-slate-400 hover:text-indigo-700'}`}
                                                        >
                                                            <ListChecks className="w-3.5 h-3.5" />
                                                            {mitBestandteilen ? `Bestandteile (${(s.bestandteile || []).length})` : 'Bestandteile festlegen'}
                                                        </button>
                                                    </div>
                                                )}
                                                {/* MONATSPAUSCHALE: jede neue Position verlangt die
                                                    ausdrueckliche Entscheidung — ohne Vorbelegung,
                                                    ohne Vorauswahl; unentschieden sperrt das
                                                    Speichern (Betreiber-Entscheidung 3). */}
                                                {flatDecisionIds.has(s.id) && (
                                                    <div className={`mt-2 flex flex-wrap items-center gap-2 text-[11px] font-bold rounded-lg border px-2 py-1.5 w-fit ${flatDecisions[s.id] ? 'border-slate-200 bg-slate-50 text-slate-600' : 'border-amber-300 bg-amber-50 text-amber-700'}`}>
                                                        <span>In der Monatspauschale enthalten:</span>
                                                        <button
                                                            onClick={() => setFlatDecisions(prev => ({ ...prev, [s.id]: 'included' }))}
                                                            className={`px-2 py-0.5 rounded border transition-colors ${flatDecisions[s.id] === 'included' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:border-indigo-400'}`}
                                                        >Ja</button>
                                                        <button
                                                            onClick={() => setFlatDecisions(prev => ({ ...prev, [s.id]: 'excluded' }))}
                                                            className={`px-2 py-0.5 rounded border transition-colors ${flatDecisions[s.id] === 'excluded' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:border-indigo-400'}`}
                                                        >Nein</button>
                                                        {!flatDecisions[s.id] && <span className="font-normal">— Entscheidung erforderlich</span>}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-right bg-indigo-50/10 border-l border-indigo-50"><input type="number" step="0.01" value={s.unitPrice} onChange={(e) => handlePriceChange(idx, e.target.value)} className="w-20 text-right font-mono text-sm border border-indigo-200 rounded py-1 px-2 focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-indigo-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" /></td>
                                            <td className={`p-2 text-center bg-indigo-50/30 border-r border-indigo-100 ${isOverrun ? 'bg-red-100 border-red-200' : ''}`}><input type="number" step={isHourlyUnit(s.unit) ? 0.5 : 1} value={s.quantityContracted} onChange={(e) => handleQuantityChange(idx, e.target.value)} className={`w-16 text-center font-bold text-indigo-700 border border-indigo-200 rounded py-1 focus:ring-2 focus:ring-indigo-500 outline-none bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isOverrun ? 'text-red-700 border-red-300 ring-2 ring-red-200' : ''}`} /></td>
                                            <td className={`p-2 text-center transition-colors ${isUnlocked ? 'bg-orange-50' : 'bg-transparent'}`}><div className="flex items-center justify-center gap-2"><input type="number" step={isHourlyUnit(s.unit) ? 0.5 : 1} value={s.quantityUsed} disabled={!isUnlocked} onChange={(e) => handleUsedQuantityChange(idx, e.target.value)} className={`w-16 text-center font-bold border rounded py-1 outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isOverrun ? 'text-red-600' : ''} ${isUnlocked ? 'bg-white border-orange-400 text-orange-700 ring-4 ring-orange-100 shadow-sm' : 'bg-slate-100 border-transparent text-slate-600 cursor-not-allowed'}`} /><button onClick={() => toggleUsedLock(s.id)} className={`p-1.5 rounded transition-all ${isUnlocked ? 'bg-orange-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-200'}`} title={isUnlocked ? "Sperren" : "Leistungsstand korrigieren (Entsperren)"}>{isUnlocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}</button></div></td>
                                            <td className="p-4 text-slate-500 text-xs">{s.unit}</td>
                                            {/* Mindeststunden: eine reine Stammangabe der Position und
                                                deshalb bewusst NICHT hinter der IST-Stand-Sperre —
                                                sie bewegt keine Menge, sie belegt nur den
                                                Buchungsdialog vor. */}
                                            <td className="p-2 text-center">
                                                {isHourlyUnit(s.unit) ? (
                                                    // NICHT type="number": ein Zahlenfeld leert e.target.value
                                                    // fuer jeden Zwischenstand, der keine gueltige Gleitkommazahl
                                                    // ist. Ein Backspace auf "2.5" liefert dem Handler "" statt
                                                    // "2." — nicht von einem geleerten Feld zu unterscheiden, und
                                                    // die vereinbarte Vorgabe waere geloescht, waehrend im Kasten
                                                    // weiter "2." steht. Gleiches Muster wie beim Auftragswert
                                                    // im Annahmedialog.
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        placeholder="1"
                                                        value={minHoursText[s.id] ?? (s.minHours ?? '')}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            setMinHoursText(prev => ({ ...prev, [s.id]: raw }));
                                                            // Halb getippte Eingaben ("0", "2.") lassen den
                                                            // Modellwert stehen — nur ein LEERES Feld nimmt
                                                            // eine vereinbarte Vorgabe zurueck.
                                                            const next = applyMinHoursInput(raw, s.minHours);
                                                            if (next !== s.minHours) updateService(idx, 'minHours', next);
                                                        }}
                                                        className="w-16 text-center font-bold text-indigo-700 border border-indigo-200 rounded py-1 focus:ring-2 focus:ring-indigo-500 outline-none bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                        title="Mindeststunden, die bei einer Buchung vorbelegt werden"
                                                    />
                                                ) : (
                                                    <span className="text-slate-300">–</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-right font-mono text-slate-600">{new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(rowTotal)}</td>
                                            <td className="p-4 text-right">
                                                <button
                                                    onClick={() => handleImmediateDelete(s.id, idx)}
                                                    className={`p-2 rounded ${loeschSperre ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:text-red-600 hover:bg-red-50'}`}
                                                    title={loeschSperre ?? 'Position löschen'}
                                                    aria-label={loeschSperre ? 'Löschen gesperrt' : 'Position löschen'}
                                                >
                                                    {loeschSperre ? <Lock className="w-4 h-4"/> : <Trash2 className="w-4 h-4"/>}
                                                </button>
                                            </td>
                                        </tr>
                                        {loeschSperre && loeschHinweisId === String(s.id) && (
                                            <tr className="bg-amber-50/60">
                                                <td colSpan={9} className="px-4 py-2">
                                                    <div role="alert" className="flex items-start gap-2 text-[11px] font-semibold text-amber-800">
                                                        <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                        <span className="flex-1">{loeschSperre}</span>
                                                        <button type="button" onClick={() => setLoeschHinweisId(null)} className="text-amber-600 hover:text-amber-900" aria-label="Hinweis schließen">
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {zeigeBestandteile && bestandteileAuf && (() => {
                                            // Ohne Netz bekannt ist nur, OB es erledigte
                                            // Bestandteile gibt (bestandteileOffen), nicht
                                            // welche — die Rueckfrage beim Entfernen sagt
                                            // dann „möglicherweise".
                                            const eintraege = vermuteteEintragsKeys(anfang);
                                            const sperreGrund = positionsSperreFuerBestandteile(s);
                                            return (
                                                <tr className="bg-slate-50/60">
                                                    <td colSpan={9} className="px-4 pb-4 pt-1">
                                                        {isAdmin && anfang && ((Number(anfang.quantityUsed) || 0) > 0 || (Number(anfang.quantityBilled) || 0) > 0) && (
                                                            <p className="mb-2 text-[11px] font-semibold text-amber-700">
                                                                Diese Position ist bereits gebucht. Offene Bestandteile halten sie bis zur Erledigung von der Abrechnung zurück.
                                                            </p>
                                                        )}
                                                        {!mitBestandteilen && sperreGrund ? (
                                                            <p className="text-[11px] text-slate-500">{sperreGrund}</p>
                                                        ) : (
                                                            <div className="space-y-3">
                                                                <BestandteileEditor
                                                                    wert={s.bestandteile}
                                                                    // Bestehende Position: Entfernen heisst ausdruecklich null
                                                                    // (sonst bliebe die Definition stehen); das Kontingent
                                                                    // bleibt hier unberuehrt — die Datenbank behaelt es bei
                                                                    // einer bestehenden Position ohnehin (S10), der Dialog
                                                                    // zeigt also, was gilt. Neue Position: kein Schluessel,
                                                                    // und mit der Liste faellt das Kontingent (S1).
                                                                    onChange={(neu) => (anfang
                                                                        ? updateService(idx, 'bestandteile', neu ?? null)
                                                                        : patchService(idx, bestandteilePatch(neu)))}
                                                                    belegteKeys={(normalisiereBestandteile(anfang?.bestandteile) ?? []).map(b => b.key)}
                                                                    keysMitEintrag={eintraege.keys}
                                                                    keysMitEintragVermutet={eintraege.vermutet}
                                                                    vorlage={localCatalog.find(c => c.lvPosition === s.lvPosition)?.bestandteile}
                                                                    disabled={!isAdmin}
                                                                    service={s}
                                                                />
                                                                {/* STUNDENKONTINGENT (intern). Nur eine NEUE
                                                                    Position nimmt den Wert beim Speichern an; bei
                                                                    einer bestehenden behaelt die Datenbank den
                                                                    alten (S10) — dort aendert ihn ein Admin im
                                                                    Bestandteile-Dialog der Position. */}
                                                                {mitBestandteilen && (
                                                                    <div className="max-w-xs">
                                                                        <StundenKontingentFeld
                                                                            wert={s.stundenKontingent}
                                                                            onChange={(k) => updateService(idx, 'stundenKontingent', k)}
                                                                            deaktiviert={!isAdmin || !!anfang}
                                                                            sperrHinweis={anfang
                                                                                ? 'Bestehende Position: ändern nur Admins im Bestandteile-Dialog der Position.'
                                                                                : 'Festlegen nur Admins.'}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex gap-4 mt-6">
                        <button onClick={() => setIsCatalogModalOpen(true)} className="flex items-center gap-2 text-sm font-bold text-indigo-600 hover:bg-indigo-50 px-5 py-2.5 rounded-xl transition-all border-2 border-indigo-200 shadow-sm"><Plus className="w-5 h-5"/> Neue Leistung aus Katalog</button>
                    </div>
                </div>

                <div className="p-6 border-t border-slate-200 bg-white flex justify-between items-center shrink-0">
                    {usedDeltas.length > 0 ? (
                        <div className="flex items-center gap-3">
                            <label className="text-xs">
                                <span className="block font-bold uppercase text-slate-500 mb-1">Leistungsdatum der Korrektur</span>
                                <input
                                    type="date"
                                    value={correctionDate}
                                    onChange={(e) => setCorrectionDate(e.target.value)}
                                    className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </label>
                            <p className="max-w-md text-[11px] leading-snug text-slate-500">
                                {usedDeltas.length === 1 ? 'Eine Position' : `${usedDeltas.length} Positionen`} mit geändertem IST-Stand
                                {' '}werden unter diesem Datum ins Leistungsprotokoll geschrieben. Es
                                entscheidet, in welchem Abrechnungsmonat die Menge erscheint —
                                bitte das tatsächliche Leistungsdatum eintragen, nicht das
                                Korrekturdatum.
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-xs text-slate-400"><Calculator className="w-4 h-4" /><span>Preise exkl. MwSt.</span></div>
                    )}
                    <div className="flex gap-3 items-center">
                        {pendingFlatIds.size > 0 && (
                            <span className="text-[11px] font-bold text-amber-600 max-w-[220px] leading-snug">
                                Monatspauschale: {pendingFlatIds.size === 1 ? 'für eine neue Position fehlt die Entscheidung ja/nein' : `für ${pendingFlatIds.size} neue Positionen fehlt die Entscheidung ja/nein`}
                            </span>
                        )}
                        {bestandteilFehler.length > 0 && (
                            <span className="text-[11px] font-semibold text-red-700 max-w-[260px] leading-snug" title={bestandteilFehler.map(f => `${f.titel}: ${f.text}`).join('\n')}>
                                Bestandteile: {bestandteilFehler[0].titel} — {bestandteilFehler[0].text}
                                {bestandteilFehler.length > 1 ? ` (+${bestandteilFehler.length - 1} weitere)` : ''}
                            </span>
                        )}
                        <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
                        <Button variant="primary" onClick={handleSave} disabled={loading || changes.length === 0 || pendingFlatIds.size > 0 || bestandteilFehler.length > 0} className="bg-indigo-600 hover:bg-indigo-700 px-8 shadow-lg shadow-indigo-100" icon={loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}>{isSyncing ? 'Synchronisierung...' : 'Änderungen speichern'}</Button>
                    </div>
                </div>
            </div>

            {isCatalogModalOpen && (
                <div className="fixed inset-0 z-[250] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden animate-scale-in">
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg flex items-center gap-2"><Database className="w-5 h-5 text-indigo-400"/> Leistungskatalog durchsuchen</h3>
                            <button onClick={() => setIsCatalogModalOpen(false)} className="hover:bg-white/10 p-2 rounded-full transition-colors"><X className="w-5 h-5"/></button>
                        </div>
                        <div className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
                            {templates.length > 0 && (
                                <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-indigo-200 shadow-sm">
                                    <LayoutTemplate className="w-4 h-4 text-indigo-500 shrink-0" />
                                    <select
                                        value={selectedTemplateId}
                                        onChange={(e) => handleSourceChange(e.target.value)}
                                        className="text-xs w-full bg-transparent outline-none font-bold text-slate-700 cursor-pointer"
                                        title="Quelle der Positionen"
                                    >
                                        <option value="">-- Ansicht: Gesamt-Katalog --</option>
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>Vorlage: {t.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="flex gap-4 items-center">
                                <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="text" placeholder="Suchen nach Position oder Titel..." className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm shadow-inner focus:ring-2 focus:ring-indigo-500 outline-none" value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} autoFocus/></div>
                                <div className="text-xs text-slate-500 bg-white px-3 py-2 rounded-lg border border-slate-200"><strong>{selectionBuffer.size}</strong> Positionen gewählt</div>
                            </div>
                            {selectedTemplateId && (
                                <p className="text-[11px] text-indigo-600 leading-snug">
                                    Positionen dieser Vorlage werden mit der <strong>in der Vorlage vereinbarten Vertragsmenge</strong> übernommen.
                                </p>
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-100 text-slate-500 font-bold uppercase text-xs sticky top-0">
                                    <tr><th className="p-4 w-12 text-center"></th><th className="p-4 w-24">Pos.</th><th className="p-4">Bezeichnung</th><th className="p-4 w-24 text-right">EP (€)</th><th className="p-4 w-20">Einh.</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredCatalog.map((item, idx) => {
                                        const isSelected = selectionBuffer.has(item.lvPosition);
                                        const isExisting = (item as any).isExisting;

                                        return (
                                            <tr
                                                // Vorlagen-Items tragen keine id (jsonb ohne id-Feld) —
                                                // ohne Rueckfall haetten alle Zeilen denselben key.
                                                key={item.id || `${item.lvPosition}-${idx}`}
                                                onClick={() => {
                                                    if (isExisting) return;
                                                    const n = new Map(selectionBuffer);
                                                    // Katalog: unveraendert 1. Vorlage: die vereinbarte
                                                    // Menge der Position (520 Std. bleiben 520).
                                                    if(n.has(item.lvPosition)) n.delete(item.lvPosition); else n.set(item.lvPosition, selectedTemplateId ? initialQuantityForSource(item) : 1);
                                                    setSelectionBuffer(n);
                                                }}
                                                className={`transition-colors ${isExisting ? 'bg-slate-50 opacity-60 cursor-not-allowed' : (isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50 cursor-pointer')}`}
                                            >
                                                <td className="p-4 text-center">
                                                    {isExisting ? (
                                                        <div className="w-5 h-5 flex items-center justify-center text-slate-400"><Lock className="w-3 h-3"/></div>
                                                    ) : (
                                                        isSelected ? <CheckSquare className="w-5 h-5 text-indigo-600"/> : <Square className="w-5 h-5 text-slate-300"/>
                                                    )}
                                                </td>
                                                <td className="p-4 font-mono text-xs text-slate-500">{item.lvPosition}</td>
                                                <td className="p-4">
                                                    <div className={`font-bold ${isExisting ? 'text-slate-400' : 'text-slate-700'}`}>{item.title}</div>
                                                    {isExisting && <div className="text-[11px] text-indigo-500 font-black uppercase tracking-widest mt-0.5">Bereits vorhanden</div>}
                                                    {selectedTemplateId && !isExisting && (
                                                        <div className="text-[11px] text-slate-400 mt-0.5">Vertragsmenge lt. Vorlage: <strong className="text-slate-600">{initialQuantityForSource(item)}</strong> {item.unit}</div>
                                                    )}
                                                </td>
                                                {/* Vorlagen-Items kommen aus jsonb — ein fehlender Preis darf die Liste nicht sprengen. */}
                                                <td className="p-4 text-right font-mono text-slate-600">{(Number(item.unitPrice) || 0).toFixed(2)}</td>
                                                <td className="p-4 text-slate-400 text-xs">{item.unit}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-5 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => setIsCatalogModalOpen(false)}>Abbrechen</Button>
                            <Button variant="primary" onClick={() => {
                                const newItems: ServiceItem[] = [];
                                selectionBuffer.forEach((qty, pos) => {
                                    const t = activeSourceItems.find(c => c.lvPosition === pos);
                                    if(t) newItems.push(createServiceItemFromSource(t, qty, generateValidUUID));
                                });
                                setServices(prev => [...prev, ...newItems]);
                                setIsCatalogModalOpen(false);
                                setSelectionBuffer(new Map());
                            }} disabled={selectionBuffer.size === 0} className="bg-indigo-600 px-8">{selectionBuffer.size} übernehmen</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
